import { useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { AlertCircle, CreditCard, Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Alert, AlertDescription } from 'components/ui/alert';
import { Button } from 'components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from 'components/ui/dialog';
import { useTheme } from 'components/core/theme-provider';
import { useConfig } from 'hooks/use-config';
import { cn } from 'lib/utils';
import { useSettingsDialogStore } from 'state/store/settings-dialog/useSettingsDialogStore';
import {
  BillingDetailsForm,
  BillingDetailsFormHandle,
} from 'features/subscription/components/billing-details-form';
import { BillingCycleToggle } from 'features/subscription/components/subscription-plan';
import { billingCycleLabel, uniquePlanIntervals } from 'features/subscription/utils/billing-cycle';
import { SEAT_POOLS_QUERY_KEY, useSeatQuote } from 'features/subscription/hooks/use-seats';
import { useGetBillingProfile } from 'features/subscription/hooks/use-subscription';
import { startCardSetup } from 'features/subscription/services/seat-service';
import { primaryButtonClass, secondaryButtonClass } from 'features/subscription/constants/button-styles';
import {
  BILLING_PROFILE_INCOMPLETE,
  defaultSeatInterval,
  formatMinor,
  PlanSeats,
  seatPriceLabel,
  utilitiesErrorMessage,
  withInterval,
} from 'features/subscription/utils/seat-pools';

/**
 * What setting a plan to `quantity` seats costs now, or why it cannot be bought. `ready` is false
 * while the price loads, while a payment still needs billing details, and whenever Utilities would
 * refuse, so the caller can hold its button.
 */
export const useSeatPrice = (entry: PlanSeats | null | undefined, quantity: number | null) => {
  const { t } = useTranslation();
  const { data, isLoading, error } = useSeatQuote(entry, quantity);
  const { data: profile } = useGetBillingProfile();
  const allBlockers = data?.blockers ?? [];
  // Billing details are asked for in place rather than listed as a blocker.
  const blockers = allBlockers.filter((blocker) => blocker.code !== BILLING_PROFILE_INCOMPLETE);
  const needsBilling =
    !!data &&
    (allBlockers.length !== blockers.length || (data.dueNowMinor > 0 && !!profile && !profile.isComplete));
  const problem = error
    ? utilitiesErrorMessage(error) || t('SEAT_PRICE_FAILED', 'The price could not be checked.')
    : '';
  return {
    quote: data,
    isLoading,
    blockers,
    needsBilling,
    problem,
    ready: !!entry && !!data && !isLoading && !problem && blockers.length === 0 && !needsBilling,
    dueNowMinor: data?.dueNowMinor ?? 0,
    dueNow: data ? formatMinor(data.dueNowMinor, data.currencyCode) : '',
  };
};

/** Opens Settings on Subscription, where the billing details and the card are. */
export const OpenBillingSettings = ({ variant = 'outline' }: { variant?: 'outline' | 'link' }) => {
  const { t } = useTranslation();
  const openSettings = useSettingsDialogStore((s) => s.openSettings);
  return (
    <Button
      type="button"
      variant={variant}
      size="sm"
      className={variant === 'link' ? 'self-start text-foreground' : 'self-start'}
      onClick={() => openSettings('subscription')}
    >
      {t('OPEN_BILLING_SETTINGS', 'Open billing settings')}
    </Button>
  );
};

export const SeatBlockers = ({ price }: { price: ReturnType<typeof useSeatPrice> }) => {
  return (
    <Alert variant="destructive">
      <AlertCircle className="h-4 w-4" />
      <AlertDescription className="flex flex-col gap-2">
        {price.blockers.map((blocker) => (
          <span key={blocker.code}>
            {blocker.message}
            {blocker.fields?.BillingProfile?.length ? ` (${blocker.fields.BillingProfile.join(', ')})` : ''}
          </span>
        ))}
        {price.problem && <span>{price.problem}</span>}
        <OpenBillingSettings />
      </AlertDescription>
    </Alert>
  );
};

/** Saves billing details in place, then prices again so the new details count. */
export const SeatBillingStep = () => {
  const queryClient = useQueryClient();
  return (
    <BillingDetailsForm
      onSaved={() => queryClient.invalidateQueries({ queryKey: [SEAT_POOLS_QUERY_KEY, 'quote'] })}
    />
  );
};

/**
 * Shown when a charge was refused for want of a saved card. Opens Utilities' card page; once a card
 * is saved there, the next seat change charges it without asking again.
 */
/** Sends the admin to Utilities' card page for a subscription; `state` says if that failed. */
export const useCardSetup = () => {
  const { configurationSettings } = useConfig();
  const [state, setState] = useState<'idle' | 'opening' | 'failed'>('idle');

  const open = async (subscriptionId: string) => {
    setState('opening');
    try {
      window.location.href = await startCardSetup(subscriptionId, configurationSettings);
    } catch {
      setState('failed');
    }
  };
  return { open, state };
};

export const AddCardStep = ({ subscriptionId }: { subscriptionId: string }) => {
  const { t } = useTranslation();
  const { open: openCardPage, state } = useCardSetup();
  const open = () => openCardPage(subscriptionId);

  return (
    <div className="flex flex-col gap-2 rounded-lg border p-4 text-sm">
      <p className="font-medium">{t('CARD_NEEDED', 'A card is needed to pay')}</p>
      <p className="text-muted-foreground">
        {t(
          'CARD_NEEDED_DESCRIPTION',
          'No card is saved yet. Add one on the secure card page; nothing is charged there. Then confirm again and the saved card is used from now on.'
        )}
      </p>
      <div className="flex flex-wrap items-center gap-2">
        <Button type="button" onClick={open} disabled={state === 'opening'}>
          {state === 'opening' ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <CreditCard className="mr-2 h-4 w-4" />}
          {t('ADD_A_CARD', 'Add a card')}
        </Button>
        <OpenBillingSettings variant="link" />
      </div>
      {state === 'failed' && (
        <p className="text-destructive">
          {t('CARD_PAGE_FAILED', 'The card page could not be opened. Try again from billing settings.')}
        </p>
      )}
    </div>
  );
};

export const SeatPriceSummary = ({ entry, quantity }: { entry: PlanSeats; quantity: number }) => {
  const { t } = useTranslation();
  const price = useSeatPrice(entry, quantity);

  if (price.isLoading) {
    return (
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" />
        {t('CHECKING_PRICE', 'Checking the price...')}
      </div>
    );
  }
  if (price.problem || price.blockers.length > 0) {
    return <SeatBlockers price={price} />;
  }
  if (price.needsBilling) {
    return <SeatBillingStep />;
  }

  const perSeat = seatPriceLabel(t, entry);
  return (
    <div className="divide-y rounded-lg border bg-muted/40 px-4 text-sm">
      {perSeat && (
        <div className="flex justify-between gap-4 py-2.5">
          <span className="text-muted-foreground">{t('PRICE_PER_SEAT', 'Price per seat')}</span>
          <span className="font-medium">{perSeat}</span>
        </div>
      )}
      <div className="flex justify-between gap-4 py-2.5">
        <span className="text-muted-foreground">{t('DUE_NOW', 'Due now')}</span>
        <span className="font-semibold">{price.dueNow}</span>
      </div>
      {price.quote && (
        <div className="flex justify-between gap-4 py-2.5">
          <span className="text-muted-foreground">{t('NEXT_RENEWAL', 'Next renewal')}</span>
          <span className="font-medium">{formatMinor(price.quote.nextRenewalMinor, price.quote.currencyCode)}</span>
        </div>
      )}
    </div>
  );
};

/** Seats to buy so people can go on a plan that has too few free. */
export interface SeatPurchaseRequest {
  entry: PlanSeats;
  /** New seats to pay for. */
  count: number;
  /** The seat count the plan moves to. */
  quantity: number;
  /** The person's name, or "3 people". */
  who: string;
  /** Whether anyone is leaving another plan, whose seat then stays free. */
  leavesSeats: boolean;
}

interface AddSeatDialogProps {
  /** Null closes the dialog. */
  request: SeatPurchaseRequest | null;
  /** Set once a charge was refused for want of a card: the subscription to save one against. */
  cardNeededFor?: string | null;
  /** Set once a purchase was refused for want of billing details. */
  billingNeeded?: boolean;
  isPending: boolean;
  onConfirm: (entry: PlanSeats) => void;
  onClose: () => void;
}

/** The seat being bought, who gets it, and what it costs now. */
const SeatOrderSummary = ({
  request,
  interval,
  price,
}: {
  request: SeatPurchaseRequest;
  interval: string;
  price: ReturnType<typeof useSeatPrice>;
}) => {
  const { t } = useTranslation();
  const plan = request.entry.name;
  // Extra seats on a running plan are charged for the rest of the period, not a full cycle.
  let priceNote = interval === 'year' ? t('PER_YEAR', 'per year') : t('PER_MONTH', 'per month');
  if (request.entry.pool) priceNote = t('DUE_NOW', 'Due now');

  return (
    <div className="flex items-start justify-between gap-4 rounded-xl border bg-muted/40 px-4 py-3.5">
      <div className="min-w-0">
        <p className="font-semibold">
          {request.count === 1
            ? t('PLAN_SEAT', { plan, defaultValue: '{{plan}} seat' })
            : t('PLAN_SEATS', { plan, count: request.count, defaultValue: '{{count}} {{plan}} seats' })}
        </p>
        <p className="truncate text-sm text-muted-foreground">
          {t('ASSIGNED_TO', { name: request.who, defaultValue: 'Assigned to {{name}}' })}
        </p>
      </div>
      <div className="shrink-0 text-right">
        {price.isLoading && <Loader2 className="ml-auto h-5 w-5 animate-spin text-muted-foreground" />}
        {!price.isLoading && price.quote && (
          <>
            <p className="text-lg font-bold">{price.dueNow}</p>
            <p className="text-sm text-muted-foreground">{priceNote}</p>
          </>
        )}
      </div>
    </div>
  );
};

export const AddSeatDialog = ({ request, cardNeededFor, billingNeeded, isPending, onConfirm, onClose }: AddSeatDialogProps) => {
  const { t } = useTranslation();
  const isDark = useTheme().resolvedTheme === 'dark';
  const [interval, setInterval] = useState('month');
  const priced = request ? withInterval(request.entry, interval) : undefined;
  const cycles = uniquePlanIntervals(request?.entry.plan?.prices);
  const price = useSeatPrice(priced, request?.quantity ?? null);
  const queryClient = useQueryClient();
  const billingRef = useRef<BillingDetailsFormHandle>(null);
  const [isSaving, setIsSaving] = useState(false);
  const plan = request?.entry.name ?? '';
  const count = request?.count ?? 0;
  const who = request?.who ?? '';
  const hasBlockers = !!price.problem || price.blockers.length > 0;
  // Missing billing details are filled in here and saved by the pay button.
  const showBilling = !hasBlockers && (price.needsBilling || !!billingNeeded);
  const canPay = !!priced && (price.ready || (showBilling && !!price.quote));
  const isBusy = isPending || isSaving;

  // A plan without a seat pool is being bought for the first time.
  let title = t('PURCHASE_ADDITIONAL_SEAT_TITLE', 'Purchase an additional seat');
  if (count > 1) title = t('PURCHASE_ADDITIONAL_SEATS_TITLE', { count, defaultValue: 'Purchase {{count}} additional seats' });
  if (!request?.entry.pool) {
    title =
      count > 1
        ? t('PURCHASE_FIRST_SEATS_TITLE', { count, defaultValue: 'Purchase your first {{count}} seats' })
        : t('PURCHASE_FIRST_SEAT_TITLE', 'Purchase your first seat');
  }

  useEffect(() => {
    if (request) setInterval(defaultSeatInterval(request.entry));
  }, [request]);

  const handlePay = async () => {
    if (!priced) return;
    if (showBilling) {
      setIsSaving(true);
      const isSaved = await billingRef.current?.save();
      setIsSaving(false);
      if (!isSaved) return;
    }
    onConfirm(priced);
  };

  return (
    <Dialog open={!!request} onOpenChange={(open) => !open && !isBusy && onClose()}>
      {/* Header and footer stay put; only the body scrolls when the billing form is long. */}
      <DialogContent className="!flex max-h-[90dvh] flex-col gap-0 overflow-hidden p-0 sm:max-w-[600px]">
        <DialogHeader className="px-6 pb-4 pt-6">
          <DialogTitle>{title}</DialogTitle>
          {/* <DialogDescription>
            {request?.entry.pool
              ? t('ADD_PLAN_SEAT_DESCRIPTION', {
                  plan,
                  name: who,
                  defaultValue:
                    'There are not enough {{plan}} seats available. Confirming adds them to your subscription, charges for the rest of this period, and puts {{name}} on {{plan}}.',
                })
              : t('FIRST_PLAN_SEAT_DESCRIPTION', {
                  plan,
                  name: who,
                  defaultValue:
                    'You have no {{plan}} seats yet. Confirming buys them and puts {{name}} on {{plan}}. If the payment needs confirming, the payment page opens next.',
                })}
            {request?.leavesSeats &&
              ` ${t('OLD_SEATS_STAY_AVAILABLE', 'Seats they leave stay yours, available for someone else.')}`}
          </DialogDescription> */}
        </DialogHeader>

        <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto px-6 pb-5 [scrollbar-width:thin] [scrollbar-color:hsl(var(--border))_transparent]">
          {request && priced && (
            <div className="flex flex-col gap-4">
              {!request.entry.pool && cycles.length > 1 && (
                <BillingCycleToggle fullWidth intervals={cycles} selectedInterval={interval} onChange={setInterval} />
              )}
              {request.entry.pool && cycles.length > 1 && (
                <p className="text-xs text-muted-foreground">
                  {t('SEATS_STAY_ON_CYCLE', {
                    cycle: billingCycleLabel(interval, (key, fallback) => t(key, fallback)).toLowerCase(),
                    defaultValue: 'These seats stay {{cycle}}. To change that, switch the plan.',
                  })}
                </p>
              )}
              <SeatOrderSummary request={request} interval={interval} price={price} />
              {hasBlockers && <SeatBlockers price={price} />}
              {showBilling && (
                <BillingDetailsForm
                  ref={billingRef}
                  hideActions
                  onSaved={() => queryClient.invalidateQueries({ queryKey: [SEAT_POOLS_QUERY_KEY, 'quote'] })}
                />
              )}
            </div>
          )}
          {cardNeededFor && <AddCardStep subscriptionId={cardNeededFor} />}
        </div>

        <div className="flex flex-col gap-3 border-t px-6 py-4">
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              className={cn('rounded-full px-6', secondaryButtonClass(isDark))}
              onClick={onClose}
              disabled={isBusy}
            >
              {t('CANCEL')}
            </Button>
            <Button
              className={cn('rounded-full px-6', primaryButtonClass(isDark))}
              onClick={handlePay}
              disabled={!canPay || isBusy || !!cardNeededFor}
            >
              {isBusy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {t('PAY_AND_ASSIGN', { amount: price.dueNow, defaultValue: 'Pay {{amount}} and assign' })}
            </Button>
          </DialogFooter>
          <p className="text-center text-xs text-muted-foreground sm:text-right">
            {t('BANK_CONFIRM_NEXT', 'If your bank needs to confirm the payment, the payment page opens next.')}
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
};
